import { useEffect, useState } from "react";

import { Switch } from "@car/ui/components/switch";

// Web Push on this device: the service worker (public/sw.js of each app) shows what the server
// sends (@car/api/web-push). Every app registers the worker on load, which also makes it
// installable; the switch lives in each app's notification bell.

/** What the server stores for this browser (push.subscribe) */
export type PushKeys = { endpoint: string; p256dh: string; auth: string };

const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const HOUR = 60 * 60 * 1000;

/** The router's history (TanStack Router's RouterHistory), as far as the update needs it */
type NavigationHistory = {
  block: (blocker: {
    blockerFn: (args: {
      currentLocation: { pathname: string };
      nextLocation: { pathname: string; href: string };
      action: string;
    }) => boolean;
    enableBeforeUnload: boolean;
  }) => () => void;
  _getBlockers: () => unknown[];
};

/**
 * Registers the worker and keeps an installed app current without reinstalling it. A deploy
 * changes sw.js (ops/serve.ts), the browser installs the new worker and it takes over at once;
 * the page itself is still the old one, and the server no longer has its lazy chunks. Nothing is
 * shown: the next page change becomes a full load of the same address, which is the new version.
 * The browser only looks for a new worker on navigation, and an installed window may stay open
 * for days: look when it comes back and every hour.
 */
export function registerServiceWorker(history: NavigationHistory) {
  if (!("serviceWorker" in navigator)) return;
  // the first worker taking over a page is not an update
  let controlled = Boolean(navigator.serviceWorker.controller);
  let updated = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!controlled) {
      controlled = true;
      return;
    }
    if (updated) return;
    updated = true;
    history.block({
      enableBeforeUnload: false,
      blockerFn: ({ currentLocation, nextLocation, action }) => {
        // stays in the page: a filter or tab in the query, back/forward (a load there would
        // rewrite the history), or another blocker guarding unsaved input (one prompt is enough)
        if (
          currentLocation.pathname === nextLocation.pathname ||
          (action !== "PUSH" && action !== "REPLACE") ||
          history._getBlockers().length > 1
        )
          return false;
        if (action === "REPLACE") location.replace(nextLocation.href);
        else location.assign(nextLocation.href);
        return true;
      },
    });
  });
  navigator.serviceWorker
    .register("/sw.js", { updateViaCache: "none" })
    .then((registration) => {
      const look = () => void registration.update().catch(() => null);
      document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && look());
      setInterval(look, HOUR);
    })
    .catch((error: unknown) => {
      console.error("[pwa] service worker not registered", error);
    });
}

/** Signing out: this browser stops receiving the account's pushes (the server drops it on 410) */
export async function dropPushSubscription() {
  if (!pushSupported()) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  await subscription?.unsubscribe().catch(() => false);
}

const keysOf = (subscription: PushSubscription): PushKeys => {
  const { keys } = subscription.toJSON();
  return { endpoint: subscription.endpoint, p256dh: keys?.p256dh ?? "", auth: keys?.auth ?? "" };
};

type PushState = "loading" | "unsupported" | "denied" | "off" | "on";

const hints: Record<PushState, string> = {
  loading: "",
  unsupported:
    "این مرورگر اعلان را پشتیبانی نمی‌کند؛ در آیفون ابتدا سایت را به صفحه اصلی اضافه کنید.",
  denied: "اعلان در تنظیمات مرورگر برای این سایت مسدود شده است.",
  off: "اعلان‌ها روی همین دستگاه هم نشان داده شود، حتی وقتی سایت بسته است.",
  on: "اعلان‌ها روی همین دستگاه نشان داده می‌شود.",
};

export function PushSwitch({
  vapidKey,
  onSubscribe,
  onUnsubscribe,
}: {
  /** null while the server has no VAPID keys: the switch is hidden */
  vapidKey: string | null | undefined;
  onSubscribe: (keys: PushKeys) => Promise<unknown>;
  onUnsubscribe: (endpoint: string) => Promise<unknown>;
}) {
  const [state, setState] = useState<PushState>("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!vapidKey) return;
    if (!pushSupported()) return setState("unsupported");
    if (Notification.permission === "denied") return setState("denied");
    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => {
        setState(subscription ? "on" : "off");
        // the server may have lost it, or this browser was another account's until now
        if (subscription) onSubscribe(keysOf(subscription)).catch(() => null);
      })
      .catch(() => setState("unsupported"));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per key: the callbacks are new on every render
  }, [vapidKey]);

  if (!vapidKey) return null;

  async function toggle(next: boolean) {
    if (!vapidKey) return;
    setBusy(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();
      if (!next) {
        if (current) {
          await onUnsubscribe(current.endpoint).catch(() => null);
          await current.unsubscribe();
        }
        setState("off");
        return;
      }
      // asked right here, inside the click (Safari refuses a prompt outside one)
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "denied" : "off");
        return;
      }
      // a subscription made with an older server key cannot be reused
      await current?.unsubscribe();
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: Uint8Array.from(
          atob(vapidKey.replaceAll("-", "+").replaceAll("_", "/")),
          (char) => char.charCodeAt(0),
        ),
      });
      await onSubscribe(keysOf(subscription));
      setState("on");
    } catch (error) {
      console.error("[pwa] push toggle failed", error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <label className="flex items-start gap-3 rounded-md px-2 py-2 text-sm">
      <span className="grid flex-1 gap-0.5">
        <span className="font-bold">اعلان روی این دستگاه</span>
        <span className="text-xs leading-5 text-muted-foreground">{hints[state]}</span>
      </span>
      <Switch
        className="mt-1"
        checked={state === "on"}
        disabled={busy || state === "loading" || state === "unsupported" || state === "denied"}
        onCheckedChange={(checked) => void toggle(checked)}
      />
    </label>
  );
}
